/**
 * Customer Fields UX hardening for restricted fields (Helium).
 * Applies inert + disabled + tabindex; syncs data-cf-field-actions / class on class-only containers.
 * Locks initial values of controls (including empty) and restores them on focus/input/change if tampered with.
 * Re-applies restrictions on the field container when a mismatch is detected.
 * Client-side only — not a security boundary; CF admin validation still required.
 * See snippets/jsc-customer-fields-security-notes.liquid.
 */
(() => {
  const RESTRICTED_SELECTOR =
    '.cf-field-container[data-cf-field-actions="disable_field"], .cf-field-container[data-cf-field-actions*="disable_field"], .cf-field-container.jsc-cf-field--disabled';

  const lockState = new WeakMap();
  const controlToContainer = new WeakMap();

  let syncInProgress = false;

  const syncDisableFieldAttribute = (container) => {
    if (!container.classList.contains('jsc-cf-field--disabled')) return;
    const existing = container.getAttribute('data-cf-field-actions');
    if (existing !== null && existing !== '') return;
    container.setAttribute('data-cf-field-actions', 'disable_field');
  };

  const getControlSnapshot = (el) => {
    const tag = el.tagName;
    if (tag === 'SELECT') {
      if (el.multiple) {
        return {
          type: 'select-multiple',
          values: Array.from(el.selectedOptions).map((o) => o.value),
        };
      }
      return { type: 'select', value: el.value };
    }
    if (tag === 'TEXTAREA') {
      return { type: 'text', value: el.value };
    }
    if (tag === 'INPUT') {
      const type = (el.type || '').toLowerCase();
      if (type === 'hidden') return null;
      if (type === 'file') {
        return { type: 'file', empty: true };
      }
      if (type === 'checkbox' || type === 'radio') {
        return { type: 'checkbox-radio', checked: el.checked, value: el.value };
      }
      return { type: 'text', value: el.value };
    }
    return null;
  };

  const applySnapshot = (el, snap) => {
    if (!snap) return;
    if (snap.type === 'select-multiple') {
      const set = new Set(snap.values);
      for (const opt of el.options) {
        opt.selected = set.has(opt.value);
      }
      return;
    }
    if (snap.type === 'select') {
      el.value = snap.value;
      return;
    }
    if (snap.type === 'checkbox-radio') {
      el.checked = snap.checked;
      return;
    }
    if (snap.type === 'file') {
      el.value = '';
      return;
    }
    if (snap.type === 'text') {
      el.value = snap.value;
    }
  };

  const snapshotMatchesElement = (el, snap) => {
    const cur = getControlSnapshot(el);
    if (!cur || !snap) return true;
    if (snap.type !== cur.type) return false;
    if (snap.type === 'select-multiple') {
      const a = [...snap.values].sort().join('\0');
      const b = [...cur.values].sort().join('\0');
      return a === b;
    }
    if (snap.type === 'select') return snap.value === cur.value;
    if (snap.type === 'checkbox-radio') return snap.checked === cur.checked;
    if (snap.type === 'file') return el.files.length === 0;
    if (snap.type === 'text') return snap.value === cur.value;
    return true;
  };

  const reapplyDomRestrictions = (container) => {
    if (!container?.classList) return;
    container.classList.add('jsc-cf-field--disabled');
    container.setAttribute('data-cf-field-actions', 'disable_field');
    container.setAttribute('inert', '');

    for (const el of container.querySelectorAll('input, select, textarea, button')) {
      if (el.type === 'hidden') continue;
      el.setAttribute('disabled', '');
      el.setAttribute('tabindex', '-1');
    }
  };

  const registerLocksForContainer = (container) => {
    for (const el of container.querySelectorAll('input, select, textarea')) {
      if (el.type === 'hidden') continue;
      const snap = getControlSnapshot(el);
      if (snap === null) continue;
      if (!lockState.has(el)) {
        lockState.set(el, snap);
        controlToContainer.set(el, container);
      }
    }
  };

  const enhanceContainer = (container) => {
    if (!container?.setAttribute) return;
    if (container.dataset.jscCfUxHardened !== 'true') {
      container.dataset.jscCfUxHardened = 'true';
      syncDisableFieldAttribute(container);
    }
    reapplyDomRestrictions(container);
    registerLocksForContainer(container);
  };

  const onLockedControlEvent = (event) => {
    if (syncInProgress) return;
    const target = event.target;
    if (!target || !lockState.has(target)) return;
    const snap = lockState.get(target);
    if (snapshotMatchesElement(target, snap)) return;

    syncInProgress = true;
    try {
      applySnapshot(target, snap);
      const c = controlToContainer.get(target);
      if (c) {
        reapplyDomRestrictions(c);
      }
    } finally {
      syncInProgress = false;
    }
  };

  const attachDelegatedGuards = () => {
    for (const type of ['focusin', 'input', 'change']) {
      document.addEventListener(type, onLockedControlEvent, true);
    }
  };

  const scanSubtree = (root) => {
    if (!root?.querySelectorAll) return;
    if (root.nodeType === Node.ELEMENT_NODE && root.matches(RESTRICTED_SELECTOR)) {
      enhanceContainer(root);
    }
    for (const node of root.querySelectorAll(RESTRICTED_SELECTOR)) {
      enhanceContainer(node);
    }
  };

  const startObserver = () => {
    if (!document.body) return;

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const added of record.addedNodes) {
          if (added.nodeType !== Node.ELEMENT_NODE) continue;
          scanSubtree(added);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    attachDelegatedGuards();
    scanSubtree(document);
    startObserver();
  };

  const schedule = () => {
    if (typeof window.CF !== 'undefined' && typeof window.CF.ready === 'function') {
      window.CF.ready(init);
      return;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  };

  schedule();
})();
